import React from "react";
import { Link } from "react-router-dom";
import "./PrivacyPolicy.css";

const EFFECTIVE_DATE = "August 10, 2026";

function PrivacyPolicy() {
  return (
    <div className="lp privacy-page">
      <header className="privacy-header">
        <Link to="/" className="privacy-logo">
          <img src={process.env.PUBLIC_URL + "/photentra-logo.png"} alt="Photentra" />
          <span>Photentra</span>
        </Link>
        <Link to="/" className="privacy-back">Back to home</Link>
      </header>

      <main className="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="privacy-updated">Effective {EFFECTIVE_DATE}</p>

        <p>
          Photentra ("Photentra," "we," "us," or "our") provides AI-powered photo management
          software for photographers and studios. This Privacy Policy explains what information
          we collect, how we use it, and the choices you have — including when you connect a
          Google Account to Photentra.
        </p>

        <h2>1. Information We Collect</h2>
        <h3>Account information</h3>
        <p>
          When you create a Photentra account, we collect your email address, username, studio
          name, and password (stored as a salted hash, never in plain text). If you sign in with
          Google or Facebook instead, we receive your name, email address, and profile photo from
          that provider.
        </p>
        <h3>Content you upload</h3>
        <p>
          Photos and videos you or your clients upload to an event gallery, along with associated
          metadata (event names, folders, guest uploads, face-tagging data used for gallery
          search).
        </p>
        <h3>Reminders and appointments</h3>
        <p>
          Titles, dates, times, and client names for reminders and appointments you create through
          Photentra's AI assistant.
        </p>
        <h3>Usage and device information</h3>
        <p>
          Basic technical data such as IP address, browser or device type, and app usage logs, used
          for security, debugging, and improving the product.
        </p>

        <h2>2. Google User Data</h2>
        <p>
          If you choose to sign in with Google, Photentra requests the following permissions:
        </p>
        <ul>
          <li>
            <strong>Basic profile (name, email, profile photo)</strong> — used to create and
            authenticate your Photentra account.
          </li>
          <li>
            <strong>Google Calendar (create events)</strong> — used <em>solely</em> to add an event
            to your primary Google Calendar when you create a reminder or appointment inside
            Photentra's AI assistant. Photentra does not read, browse, modify, or delete any other
            events already on your calendar, and does not access any other Google Calendar you own.
          </li>
        </ul>
        <p>
          <strong>Limited Use disclosure:</strong> Photentra's use and transfer of information
          received from Google APIs to any other app will adhere to the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements. We do not sell Google user data, and we do not
          use it for advertising.
        </p>
        <p>
          You can revoke Photentra's access to your Google Account at any time from{" "}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
            Google Account → Security → Third-party access
          </a>
          . Revoking access stops future calendar syncing; it does not delete events already
          created on your calendar or your Photentra account itself.
        </p>

        <h2>3. How We Use Your Information</h2>
        <ul>
          <li>To provide and operate the Photentra service (galleries, uploads, AI assistant, invoicing).</li>
          <li>To send transactional email — password resets, and reminder/appointment alerts.</li>
          <li>To send push notifications for reminders and appointments you create, if you enable them.</li>
          <li>To create Google Calendar events for reminders/appointments, if you've connected Google Calendar.</li>
          <li>To detect, prevent, and respond to fraud, abuse, or security issues.</li>
          <li>To improve and troubleshoot the product.</li>
        </ul>

        <h2>4. Third-Party Services We Use</h2>
        <p>Photentra relies on the following processors to operate:</p>
        <ul>
          <li><strong>Google / Facebook</strong> — optional social sign-in.</li>
          <li><strong>Google Calendar API</strong> — optional reminder/appointment sync, described above.</li>
          <li><strong>Amazon Web Services (S3)</strong> — secure storage for uploaded photos and videos.</li>
          <li><strong>Google Gemini API</strong> — powers the in-app AI assistant that interprets your chat messages (e.g. to create a reminder).</li>
          <li><strong>Expo push notification service</strong> — delivers Android push notifications, if enabled.</li>
          <li><strong>Email delivery provider (SMTP)</strong> — sends password-reset and reminder/appointment emails.</li>
        </ul>
        <p>
          Each of these providers processes data only as needed to perform the function above, and
          is bound by its own privacy and security terms.
        </p>

        <h2>5. Data Retention</h2>
        <p>
          We retain your account and content for as long as your account is active. If you delete
          your account, we delete your account data and uploaded content within a reasonable
          period, except where retention is required for legal, security, or fraud-prevention
          purposes.
        </p>

        <h2>6. Your Choices and Rights</h2>
        <ul>
          <li>Access or update your account details from within the app.</li>
          <li>Disconnect Google Calendar access at any time (see Section 2).</li>
          <li>Request deletion of your account and associated data by contacting us (below).</li>
          <li>Disable push notifications from your device's notification settings.</li>
        </ul>

        <h2>7. Security</h2>
        <p>
          We use industry-standard measures — encrypted storage, hashed passwords, and access
          controls — to protect your information. No method of transmission or storage is 100%
          secure, and we cannot guarantee absolute security.
        </p>

        <h2>8. Children's Privacy</h2>
        <p>
          Photentra is intended for photography businesses and their adult clients. We do not
          knowingly collect personal information from children under 13.
        </p>

        <h2>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Material changes will be reflected
          by updating the "Effective" date above.
        </p>

        <h2>10. Contact Us</h2>
        <p>
          Questions about this Privacy Policy or your data? Contact us at{" "}
          <a href="mailto:photentra@gmail.com">photentra@gmail.com</a>.
        </p>
      </main>
    </div>
  );
}

export default PrivacyPolicy;
